import { useState } from "react";
import dynamic from "next/dynamic";
import { TranscriptChunkFallback } from "./transcriptLoadGate";
import { SiLinear } from "@icons-pack/react-simple-icons";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { Button } from "@repo/blacksmith-ui/button";
import { cn } from "@repo/blacksmith-ui/cn";

const SessionMarkdown = dynamic(
  () => import("./SessionMarkdown").then((m) => m.SessionMarkdown),
  { ssr: false, loading: TranscriptChunkFallback },
);

export interface LinearComment {
  author?: string;
  body: string;
}

export interface ParsedLinearPrompt {
  identifier: string;
  title: string;
  description?: string;
  teamName?: string;
  projectName?: string;
  labels: string[];
  /** The comment that mentioned the agent; absent when the issue was delegated. */
  directive?: LinearComment;
  /** Context comments, excluding the directive. */
  comments: LinearComment[];
}

/**
 * Decode XML entities and tidy up trailing whitespace. Preserves line
 * breaks so markdown content (headings, lists, fenced code) survives
 * round-tripping through the parser.
 */
function decodeXmlText(value: string): string {
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Collapse a single-line value (title, label, author). All whitespace,
 * including newlines, becomes a single space. Use for short attribute-like
 * fields, never for description or comment bodies (which are markdown).
 */
function flattenInline(value: string): string {
  return decodeXmlText(value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " "));
}

/**
 * Pull a multi-line block out of an XML wrapper without flattening its
 * internal whitespace. The block is expected to be markdown (Linear sends
 * issue descriptions and comment bodies as markdown inside the prompt
 * context XML), so we strip only the outermost tag and keep newlines and
 * inline markdown intact.
 */
function extractMarkdownBlock(
  content: string,
  tag: string,
): string | undefined {
  const match = content.match(new RegExp(`<${tag}[^>]*>([\\s\\S]*?)</${tag}>`));
  if (!match?.[1]) return undefined;
  const inner = decodeXmlText(match[1]);
  return inner.length > 0 ? inner : undefined;
}

function extractInlineTag(content: string, tag: string): string | undefined {
  const match = content.match(new RegExp(`<${tag}[^>]*>([\\s\\S]*?)</${tag}>`));
  return match?.[1] ? flattenInline(match[1]) : undefined;
}

function extractAttribute(
  content: string,
  tag: string,
  attribute: string,
): string | undefined {
  const match = content.match(
    new RegExp(`<${tag}[^>]*\\s${attribute}="([^"]*)"[^>]*>`),
  );
  return match?.[1] ? decodeXmlText(match[1]) : undefined;
}

function extractAllInlineTags(content: string, tag: string): string[] {
  return Array.from(
    content.matchAll(new RegExp(`<${tag}[^>]*>([\\s\\S]*?)</${tag}>`, "g")),
  )
    .map((match) => (match[1] ? flattenInline(match[1]) : ""))
    .filter(Boolean);
}

/**
 * Rewrite Linear-specific mention markup into plain markdown so
 * SessionMarkdown renders readable `@name` tags instead of literal XML.
 *
 * Linear's promptContext example
 * (https://linear.app/developers/agent-interaction) shows that
 * user mentions inside descriptions and comments arrive as
 * `<user id="<uuid>">display-name</user>` XML wrappers:
 *
 *     <comment author="John Doe" created-at="...">
 *       <user id="df3fc33e-...">botcoder</user> Please implement this
 *     </comment>
 *
 * SessionMarkdown's `escapeAngleBrackets` step strips any tag that isn't
 * in `SAFE_HTML_TAGS`, so without preprocessing the user would see a
 * literal `<user id="...">botcoder</user>` token in the preview. Strip
 * the wrapper and prefix the inner display-name with `@`.
 *
 * Also handle the bare-URL form documented in
 * https://linear.app/developers/graphql ("mentions can be created in
 * Markdown by using the plain URL of the resource"). That's the format
 * agents send back to Linear in Agent Activity content, and it can also
 * appear in descriptions copy-pasted from Markdown outside the editor.
 * Convert profile URLs to `[@<slug>](url)` and issue URLs to
 * `[<issue-id>](url)`. URLs already wrapped in markdown link `[text](url)`,
 * autolink `<url>`, or image `![alt](url)` syntax are left alone via the
 * negative lookbehind on `([<` so we never double-wrap.
 */
export function normalizeLinearMentions(text: string): string {
  // <user id="...">display-name</user> -> @display-name.
  // The `id` attribute is dropped because we don't surface user IDs in
  // the preview; the display-name alone is what a human recognizes.
  let result = text.replace(
    /<user\b[^>]*>([\s\S]*?)<\/user>/g,
    (_, name: string) => {
      const trimmed = name.trim();
      return trimmed ? `@${trimmed}` : "";
    },
  );

  // <issue id="..." href="https://linear.app/.../BLA-1">BLA-1</issue> -> [BLA-1](href).
  result = result.replace(
    /<issue\b([^>]*)>([\s\S]*?)<\/issue>/g,
    (_, attributes: string, label: string) => {
      const trimmed = label.trim();
      const href = attributes.match(/\bhref="([^"]*)"/)?.[1];
      return href && trimmed ? `[${trimmed}](${href})` : trimmed;
    },
  );

  // Bare profile URLs are matched only when not already inside another
  // markdown construct. The slug character class deliberately omits `.`
  // so a trailing sentence period like ".../profiles/john." doesn't get
  // pulled into the link href (Linear slugs are typically kebab-case or
  // use underscores; dotted slugs are rare and not worth the false
  // positives).
  const profilePattern =
    /(?<![([<])https:\/\/linear\.app\/[^/\s]+\/profiles\/([A-Za-z0-9_-]+)/g;
  // Same shape for issue mentions: `.../issue/<ID>[/<slug>]`. The id is
  // an uppercase team prefix + `-` + digits. The optional `/slug` tail
  // (Linear's pretty-URL component) is consumed so the full URL lands in
  // the link href, but it's restricted to slug-like chars so trailing
  // punctuation is left outside the match.
  const issuePattern =
    /(?<![([<])https:\/\/linear\.app\/[^/\s]+\/issue\/([A-Z][A-Z0-9]*-\d+)(?:\/[A-Za-z0-9_-]+)?/g;

  return result
    .replace(profilePattern, (url, slug) => `[@${slug}](${url})`)
    .replace(issuePattern, (url, issueId) => `[${issueId}](${url})`);
}

function extractComments(content: string): LinearComment[] {
  return Array.from(
    content.matchAll(/<comment\b([^>]*)>([\s\S]*?)<\/comment>/g),
  )
    .map((match) => {
      const author = match[1]?.match(/\bauthor="([^"]*)"/)?.[1];
      return {
        author: author ? flattenInline(author) : undefined,
        body: match[2] ? normalizeLinearMentions(decodeXmlText(match[2])) : "",
      };
    })
    .filter((comment) => comment.body.length > 0);
}

const DIRECTIVE_THREAD_PATTERN =
  /<primary-directive-thread\b[^>]*>([\s\S]*?)<\/primary-directive-thread>/;

export function parseLinearPromptContext(
  content: string,
): ParsedLinearPrompt | null {
  if (!content.trim().startsWith("<issue")) return null;

  const identifier = extractAttribute(content, "issue", "identifier");
  const title = extractInlineTag(content, "title");
  if (!identifier || !title) return null;

  // Linear creates the session on the comment that mentions the agent, so it
  // is the newest comment in the primary directive thread.
  const directiveThread = extractComments(
    content.match(DIRECTIVE_THREAD_PATTERN)?.[1] ?? "",
  );
  const directive = directiveThread[directiveThread.length - 1];
  const comments = [
    ...directiveThread.slice(0, -1),
    ...extractComments(content.replace(DIRECTIVE_THREAD_PATTERN, "")),
  ].slice(0, 3);

  const rawDescription = extractMarkdownBlock(content, "description");
  return {
    identifier,
    title,
    description: rawDescription
      ? normalizeLinearMentions(rawDescription)
      : undefined,
    teamName: extractAttribute(content, "team", "name"),
    projectName: extractAttribute(content, "project", "name"),
    labels: extractAllInlineTags(content, "label"),
    directive,
    comments,
  };
}

/** Roughly how many characters of description we show before collapsing. */
const DESCRIPTION_PREVIEW_CHARS = 320;

/**
 * Truncate a description for the collapsed preview without slicing through
 * markdown image or link syntax. Linear embeds attachments as
 * `![](https://uploads.linear.app/...?signature=…)` markdown, and the signed
 * URLs are routinely 400-600 chars long, well past the 320-char preview
 * threshold. A naive `slice(0, 320)` lands in the middle of the URL, leaves
 * the construct without its closing `)`, and the markdown renderer drops the
 * malformed image entirely, so users see nothing until they click "Show
 * more". When the cut would land inside such a construct we either extend
 * it to include the whole construct (preferred for images so the visual
 * content appears in the preview, capped at ~2x the threshold to keep the
 * card bounded) or back off to just before it.
 */
export function truncateDescription(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;

  // Matches markdown link `[text](url)` and image `![alt](url)` constructs.
  // The URL portion tolerates anything except `)`, which is fine for Linear
  // URLs and typical web links: they don't contain literal `)` characters.
  const constructPattern = /!?\[[^\]]*\]\([^)]*\)/g;
  let cut = maxChars;
  let match: RegExpExecArray | null;

  while ((match = constructPattern.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (start >= cut) break; // Construct starts after the cut, no overlap.
    if (end > cut) {
      const isImage = match[0].startsWith("!");
      // Pull images into the preview when their full markdown stays within
      // roughly 2x the threshold; otherwise drop them so we don't render
      // broken syntax. Links always get dropped on overlap because the
      // visible text would already be partly cut by the time we reach them.
      cut = isImage && end <= maxChars * 2 ? end : start;
    }
  }

  if (cut >= text.length) return text;
  return `${text.slice(0, cut).trimEnd()}\u2026`;
}

interface LinearMessagePreviewProps {
  content: string;
  className?: string;
}

/** Circular avatar showing a comment author's initials. */
function Initials({ name }: { name?: string }) {
  const initials = (name ?? "?")
    .split(/\s+/)
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span
      aria-hidden
      className="flex size-5 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-[9px] font-semibold text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300"
    >
      {initials}
    </span>
  );
}

/**
 * Team, labels, description and context comments. Description and comment
 * bodies render as markdown so Linear's headings, code blocks and links
 * survive; long descriptions collapse behind "Show more".
 */
function LinearIssueDetails({ parsed }: { parsed: ParsedLinearPrompt }) {
  const [expanded, setExpanded] = useState(false);

  const description = parsed.description ?? "";
  // Compute the truncated form unconditionally so the toggle state survives
  // expansion. Comparing the truncated form to the full description is what
  // tells us "Show more / Show less" is meaningful: truncateDescription may
  // extend the cut to include a full image (or back off entirely), so the
  // raw length isn't a reliable signal on its own. If we read this off the
  // currently visible string instead, the comparison flips to false the
  // moment the user expands, and the "Show less" button unmounts.
  const truncatedDescription = truncateDescription(
    description,
    DESCRIPTION_PREVIEW_CHARS,
  );
  const isLongDescription = truncatedDescription !== description;
  const visibleDescription = expanded ? description : truncatedDescription;

  const metaLine = [parsed.teamName, parsed.projectName, ...parsed.labels]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="space-y-2">
      {metaLine && (
        <p className="type-body-xs-regular text-neutral-500 dark:text-neutral-400">
          {metaLine}
        </p>
      )}

      {description && (
        <div>
          <SessionMarkdown content={visibleDescription} />
          {isLongDescription && (
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
            >
              {expanded ? (
                <>
                  <ChevronUp className="size-3" aria-hidden />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="size-3" aria-hidden />
                  Show more
                </>
              )}
            </button>
          )}
        </div>
      )}

      {parsed.comments.length > 0 && (
        <div className="pt-1">
          {parsed.comments.map((comment, index) => (
            <div
              key={`${comment.author ?? "comment"}-${index}`}
              className="flex gap-2.5"
            >
              <div className="flex flex-col items-center">
                <Initials name={comment.author} />
                {index < parsed.comments.length - 1 && (
                  <span className="mt-1 w-px flex-1 bg-[var(--color-border)]" />
                )}
              </div>
              <div
                className={cn(
                  "min-w-0 flex-1",
                  index < parsed.comments.length - 1 ? "pb-3" : "pb-0",
                )}
              >
                {comment.author && (
                  <p className="mb-0.5 type-body-xs-medium text-neutral-600 dark:text-neutral-300">
                    {comment.author}
                  </p>
                )}
                <SessionMarkdown content={comment.body} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A comment-triggered session leads with the comment that mentioned the agent
 * and tucks the issue into a collapsible attachment row. A delegated issue has
 * no such comment, so the issue itself is the ask and renders expanded.
 */
export function LinearMessagePreview({
  content,
  className,
}: LinearMessagePreviewProps) {
  const parsed = parseLinearPromptContext(content);
  const [issueExpanded, setIssueExpanded] = useState(false);

  if (!parsed) return null;

  const { directive } = parsed;
  const hasIssueDetails = Boolean(
    parsed.description ||
    parsed.teamName ||
    parsed.projectName ||
    parsed.labels.length > 0 ||
    parsed.comments.length > 0,
  );
  const canExpandIssue = Boolean(directive) && hasIssueDetails;
  const showIssueDetails = hasIssueDetails && (!directive || issueExpanded);
  const issueSummary = (
    <>
      <SiLinear className="size-3 shrink-0 text-[#5e6ad2]" aria-hidden />
      <span className="shrink-0 type-body-xxs-regular font-mono text-neutral-500 dark:text-neutral-400">
        {parsed.identifier}
      </span>
      <span
        title={parsed.title}
        className="min-w-0 truncate type-body-s-medium text-neutral-900 dark:text-neutral-100"
      >
        {parsed.title}
      </span>
    </>
  );

  return (
    <div className={cn("min-w-0", className)}>
      {directive && (
        <div className="mb-3">
          <div className="flex min-w-0 items-center gap-2">
            <Initials name={directive.author} />
            {directive.author && (
              <span className="truncate type-body-xs-medium text-neutral-700 dark:text-neutral-200">
                {directive.author}
              </span>
            )}
            <span className="flex shrink-0 items-center gap-1 type-body-xs-regular text-neutral-500 dark:text-neutral-400">
              via
              <SiLinear className="size-3 text-[#5e6ad2]" aria-hidden />
              Linear comment
            </span>
          </div>
          <div className="mt-1.5">
            <SessionMarkdown content={directive.body} />
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="flex items-center gap-2 px-2.5 py-1.5">
          {canExpandIssue ? (
            <Button
              type="button"
              variant="inline"
              onClick={() => setIssueExpanded((prev) => !prev)}
              aria-expanded={issueExpanded}
              className="flex min-w-0 flex-1 items-center justify-start gap-2 whitespace-normal text-left"
            >
              {issueSummary}
              <ChevronRight
                className={cn(
                  "ml-auto size-3.5 shrink-0 text-neutral-400 transition-transform dark:text-neutral-500",
                  issueExpanded && "rotate-90",
                )}
                aria-hidden
              />
            </Button>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {issueSummary}
            </div>
          )}
        </div>
        {showIssueDetails && (
          <div className="border-t border-[var(--color-border)] px-2.5 py-2.5">
            <LinearIssueDetails parsed={parsed} />
          </div>
        )}
      </div>
    </div>
  );
}
